import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Plus } from "lucide-react";

import UserProfileModal from "@/components/UserProfileModal";
import AdminUserEditModal from "@/components/AdminUserEditModal";
import AdminAddUserModal from "@/components/AdminAddUserModal";

import UsersHeader from "@/components/admin-users/UsersHeader";
import UsersToolbar from "@/components/admin-users/UsersToolbar";
import UsersTable from "@/components/admin-users/UsersTable";
import UsersMobileCards from "@/components/admin-users/UsersMobileCards";
import UsersPagination from "@/components/admin-users/UsersPagination";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminUsersRedesigned = () => {
  const navigate = useNavigate();

  // Core list state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(15);
  const [totalUsers, setTotalUsers] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [stats, setStats] = useState({ total_users: 0, new_users: 0, pending_users: 0, approved_users: 0, suspended_users: 0, bounced_users: 0 });

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showPublicProfile, setShowPublicProfile] = useState(false);

  // Admin check
  useEffect(() => {
    if (!localStorage.getItem("shootzu_admin")) navigate("/szadmin");
  }, [navigate]);

  // Debounce search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(searchQuery); setCurrentPage(1); }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchUsers(); }, [statusFilter, currentPage, debouncedSearch]);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/admin/stats`);
      setStats(r.data);
    } catch { /* ignore */ }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${BACKEND}/api/admin/users/all?page=${currentPage}&limit=${usersPerPage}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
      const r = await axios.get(url);
      setUsers(r.data.users || []);
      setTotalUsers(r.data.total || 0);
    } catch {
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage, debouncedSearch, usersPerPage]);

  // Action handlers
  const updateApprovalStatus = async (userId, newStatus, sendNotification = true) => {
    try {
      await axios.put(`${BACKEND}/api/admin/users/${userId}/approval`, { approval_status: newStatus, send_notification: sendNotification });
      toast.success(`Status updated to ${newStatus}${newStatus === "approved" && !sendNotification ? " (no notification)" : ""}`);
      fetchStats(); fetchUsers();
    } catch { toast.error("Failed to update approval status"); }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Delete user: ${userEmail}?\n\nThis cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${BACKEND}/api/admin/users/${userId}/delete`);
      if (r.data.success) { toast.success("User deleted"); fetchStats(); fetchUsers(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete user"); }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) return;
    if (!window.confirm(`Delete ${selectedUsers.size} user(s)? This cannot be undone.`)) return;
    try {
      const r = await axios.post(`${BACKEND}/api/admin/users/bulk-delete`, { user_ids: Array.from(selectedUsers) });
      if (r.data.success) { toast.success(`Deleted ${r.data.deleted_count} user(s)`); setSelectedUsers(new Set()); fetchUsers(); fetchStats(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const handleRemoveBounce = async (userId, userName) => {
    if (!window.confirm(`Remove bounce tag from ${userName}? This will re-enable email sending.`)) return;
    try {
      await axios.post(`${BACKEND}/api/admin/users/${userId}/reactivate-email`);
      toast.success(`Bounce tag removed from ${userName}`);
      fetchUsers(); fetchStats();
    } catch { toast.error("Failed to remove bounce tag"); }
  };

  const handlePasswordReset = async (userId, userEmail) => {
    if (!window.confirm(`Send password reset email to ${userEmail}?`)) return;
    try {
      const r = await axios.post(`${BACKEND}/api/admin/trigger-password-reset`, { user_email: userEmail });
      if (r.data.success) toast.success("Password reset email sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send password reset"); }
  };

  const handleLoginAs = (user) => {
    if (user.account_status === "suspended" || user.approval_status === "suspended") {
      toast.error("Cannot login as a suspended user"); return;
    }
    localStorage.setItem("admin_session_backup", JSON.stringify({ token: localStorage.getItem("shootzu_admin") || "admin_session_token", timestamp: new Date().toISOString() }));
    localStorage.setItem("shootzu_token", `admin_viewing_as_${user.id}`);
    localStorage.setItem("shootzu_user", JSON.stringify(user));
    localStorage.setItem("admin_viewing_as_user", "true");
    const name = user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
    toast.success(`Logged in as ${name}`);
    navigate("/dashboard");
  };

  const handleViewProfile = async (user) => {
    try {
      const r = await axios.get(`${BACKEND}/api/profile/${user.id}`);
      setSelectedUser({ ...user, portfolio_images: r.data.portfolio_images || [], portfolio_image_keys: r.data.portfolio_image_keys || [] });
    } catch {
      setSelectedUser(user);
    }
    setShowPublicProfile(true);
  };

  const toggleSelectUser = (userId) => {
    const n = new Set(selectedUsers);
    n.has(userId) ? n.delete(userId) : n.add(userId);
    setSelectedUsers(n);
  };

  const toggleSelectAll = () => {
    setSelectedUsers(selectedUsers.size === users.length ? new Set() : new Set(users.map((u) => u.id)));
  };

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const viewCallbacks = {
    onViewProfile: handleViewProfile,
    onRemoveBounce: handleRemoveBounce,
    onLoginAs: handleLoginAs,
    onEdit: (user) => { setSelectedUser(user); setShowUserModal(true); },
    onPasswordReset: handlePasswordReset,
    onDelete: handleDeleteUser,
    onApprovalChange: updateApprovalStatus,
  };

  const totalLabel =
    `${totalUsers.toLocaleString()} ${totalUsers === 1 ? "user" : "users"}` +
    (statusFilter !== "all" ? ` · ${statusFilter.charAt(0).toUpperCase()}${statusFilter.slice(1)}` : "");

  return (
    <div>
      {/* Sticky page header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div>
          <h1 className="sz-h1 m-0">User Management</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">{totalLabel}</div>
        </div>
        <button onClick={() => setShowAddUserModal(true)} className="sz-btn sz-btn-primary">
          <Plus className="w-4 h-4" />Add User
        </button>
      </header>

      <main className="max-w-[1400px] px-[30px] pt-[22px] pb-[56px]">
        {/* Stat cards */}
        <UsersHeader stats={stats} loading={loading && users.length === 0} />

        {/* Sticky toolbar */}
        <div className="sticky top-[73px] z-10 mt-[18px]">
          <UsersToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={(s) => { setStatusFilter(s); setCurrentPage(1); }}
            selectedCount={selectedUsers.size}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedUsers(new Set())}
            stats={stats}
          />
        </div>

        {/* Content */}
        <div className="mt-4">
          {loading ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : users.length === 0 ? (
            <div className="sz-card text-center py-16">
              <div className="w-14 h-14 bg-[#EEF0F4] rounded-full flex items-center justify-center mx-auto mb-3"><Users className="w-7 h-7 text-[#A8AEBC]" /></div>
              <p className="font-semibold text-[#0C1120] mb-1">No users found</p>
              <p className="text-sm text-[#8A90A2]">{searchQuery || statusFilter !== "all" ? "Try adjusting your search or filters" : "No users registered yet"}</p>
            </div>
          ) : (
            <>
              <UsersTable users={users} selectedUsers={selectedUsers} onToggleSelect={toggleSelectUser} onToggleSelectAll={toggleSelectAll} {...viewCallbacks} />
              <UsersMobileCards users={users} selectedUsers={selectedUsers} onToggleSelect={toggleSelectUser} {...viewCallbacks} />
              <UsersPagination currentPage={currentPage} totalPages={totalPages} totalUsers={totalUsers} usersPerPage={usersPerPage} onPageChange={setCurrentPage} />
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showUserModal && selectedUser && (
        <AdminUserEditModal user={selectedUser} isOpen={showUserModal} onClose={() => { setShowUserModal(false); setSelectedUser(null); }} onSaved={fetchUsers} />
      )}

      <UserProfileModal
        user={selectedUser}
        isOpen={showPublicProfile}
        onClose={() => { setShowPublicProfile(false); setSelectedUser(null); }}
        onUpdate={() => { fetchUsers(); setShowPublicProfile(false); setSelectedUser(null); }}
        onDelete={fetchUsers}
      />

      {showAddUserModal && (
        <AdminAddUserModal isOpen={showAddUserModal} onClose={() => setShowAddUserModal(false)} onAdded={fetchUsers} />
      )}
    </div>
  );
};

export default AdminUsersRedesigned;
